import React, { useState } from 'react';
import { statusTone } from './mccConstants';
import MccContentCard from './MccContentCard';

/**
 * Visual Kanban board with HTML5 drag-and-drop.
 *
 * Props:
 *   stages: [string]  ordered column keys
 *   items: [item]
 *   variant: 'project' | 'idea' | 'request'
 *   nameOf: (uid) => name
 *   onOpen: (item) => void
 *   onMove: (item, newStatus) => Promise<void>   optional — enables DnD
 */
export default function MccKanban({ stages, items, variant = 'project', nameOf, onOpen, onMove }) {
  const [dragging, setDragging] = useState(null); // id
  const [dragTarget, setDragTarget] = useState(null); // stage

  const grouped = stages.map((s) => ({
    stage: s,
    tone: statusTone(s),
    items: items.filter((i) => i.status === s),
  }));

  const onDragStart = (e, item) => {
    if (!onMove) return;
    setDragging(item.id);
    try {
      e.dataTransfer.setData('text/plain', item.id);
      e.dataTransfer.effectAllowed = 'move';
    } catch { /* ignore */ }
  };

  const onDragOver = (e, stage) => {
    if (!onMove) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragTarget !== stage) setDragTarget(stage);
  };

  const onDrop = async (e, stage) => {
    e.preventDefault();
    const id = e.dataTransfer.getData('text/plain');
    const item = items.find((i) => i.id === id);
    setDragging(null); setDragTarget(null);
    if (!onMove || !item || item.status === stage) return;
    try { await onMove(item, stage); }
    catch { /* toast handled upstream */ }
  };

  return (
    <div className="flex gap-3 overflow-x-auto pb-4 -mx-1 px-1" data-testid={`mcc-kanban-${variant}`}>
      {grouped.map(({ stage, tone, items: colItems }) => {
        const isTarget = dragTarget === stage;
        return (
          <div
            key={stage}
            className="w-[280px] flex-shrink-0"
            onDragOver={(e) => onDragOver(e, stage)}
            onDrop={(e) => onDrop(e, stage)}
            onDragLeave={() => setDragTarget(null)}
          >
            <div
              className="rounded-t-xl px-3 py-2 flex items-center justify-between text-[11px] font-bold uppercase tracking-wider"
              style={{ background: tone.bg, color: tone.ink }}
            >
              <span className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: tone.accent }} />
                {tone.label}
              </span>
              <span className="opacity-70">{colItems.length}</span>
            </div>
            <div
              className={`border border-t-0 rounded-b-xl p-2 space-y-2 min-h-[100px] transition-colors ${
                isTarget ? 'bg-indigo-50 border-indigo-400 ring-2 ring-indigo-300' : 'bg-slate-50/70 border-slate-200'
              }`}
            >
              {colItems.length === 0 ? (
                <p className="text-center text-[10px] text-slate-400 py-6">
                  {isTarget ? 'Drop here' : 'Empty'}
                </p>
              ) : colItems.map((it) => (
                <div
                  key={it.id}
                  draggable={!!onMove}
                  onDragStart={(e) => onDragStart(e, it)}
                  onDragEnd={() => { setDragging(null); setDragTarget(null); }}
                  className={`${dragging === it.id ? 'opacity-40' : ''} ${onMove ? 'cursor-move' : ''}`}
                  data-testid={`mcc-kanban-card-${it.id}`}
                >
                  <MccContentCard
                    item={it}
                    variant={variant}
                    nameOf={nameOf}
                    onClick={() => onOpen(it)}
                    testid={`mcc-card-${it.id}`}
                  />
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
